import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

export default function Login() {
  const rawRole = new URLSearchParams(useLocation().search).get("role");
  const [role,     setRole]     = useState(rawRole === "teacher" ? "teacher" : "student"); // "student" | "teacher"
  const [form,     setForm]     = useState({ identifier:"", password:"" });
  const [err,      setErr]      = useState("");
  const [unverified, setUnverified] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [showPwd,  setShowPwd]  = useState(false);

  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const rawRedirect = new URLSearchParams(location.search).get("redirect");
  const redirect = rawRedirect?.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;

  const isStudent = role === "student";

  const inp = {
    width:"100%", marginTop:6, padding:"12px 14px", borderRadius:10,
    border:"1px solid #1e293b", background:"#0d0d0d", color:"#f1f5f9",
    fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit",
  };

  const submit = async e => {
    e.preventDefault();
    setErr("");
    setUnverified(false);
    setResendDone(false);

    if (!form.identifier.trim()) {
      setErr(isStudent ? "Phone number daalna zaroori hai" : "Email address daalna zaroori hai");
      return;
    }
    if (!form.password) {
      setErr("Password daalna zaroori hai");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        identifier: form.identifier.trim(),
        password:   form.password,
      });
      if (data.user.role === "admin") {
        setErr("Admins must use the Admin Login page.");
        setLoading(false);
        return;
      }
      login(data.token, data.user);
      nav(redirect || (data.user.role === "teacher" ? "/dashboard" : "/search"));
    } catch (e) {
      if (e.response?.data?.email_unverified) setUnverified(true);
      setErr(e.response?.data?.message || "Invalid credentials. Please try again.");
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (!form.identifier.trim()) return;
    setResendLoading(true);
    try {
      await api.post("/auth/resend-verification", { email: form.identifier.trim() });
      setResendDone(true);
    } catch {
      setResendDone(true);
    }
    setResendLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
      <Helmet>
        <title>Login | PadhaiPass</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={{ background:"#111111", borderRadius:16, border:"1px solid #1e293b", padding:"32px 24px", width:"100%", maxWidth:400 }}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:6 }}>
            <Logo size={28} />
          </div>
          <div style={{ fontSize:20, fontWeight:700, color:"#f1f5f9", marginBottom:4 }}>Welcome Back</div>
          <div style={{ fontSize:13, color:"#94a3b8" }}>Login to your account</div>
        </div>

        {/* Role selector */}
        <div style={{ display:"flex", gap:8, marginBottom:22, background:"#0d0d0d", borderRadius:12, padding:4 }}>
          {[
            { key:"student", label:"👨‍👩‍👧 Parent / Student" },
            { key:"teacher", label:"🧑‍🏫 Teacher" },
          ].map(r => (
            <button
              key={r.key}
              type="button"
              onClick={() => { setRole(r.key); setForm({ identifier:"", password:"" }); setErr(""); setUnverified(false); }}
              style={{
                flex:1, padding:"10px 8px", borderRadius:9,
                border:"none",
                background: role === r.key ? "linear-gradient(135deg,#f97316,#ea580c)" : "transparent",
                color: role === r.key ? "#fff" : "#94a3b8",
                fontSize:13, fontWeight:700, cursor:"pointer", transition:"all .18s",
              }}>
              {r.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} noValidate>

          {/* Identifier field */}
          <div style={{ marginBottom:14 }}>
            {isStudent ? (
              <>
                <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>WhatsApp Number</label>
                <div style={{ position:"relative", marginTop:6 }}>
                  <div style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#94a3b8", fontWeight:600 }}>+91</div>
                  <input
                    type="tel"
                    style={{ ...inp, paddingLeft:44, marginTop:0 }}
                    value={form.identifier}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setForm(f => ({ ...f, identifier: val }));
                      setErr("");
                    }}
                    placeholder="9876500000"
                    maxLength={10}
                  />
                </div>
              </>
            ) : (
              <>
                <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>Email Address</label>
                <input
                  type="email"
                  style={inp}
                  value={form.identifier}
                  onChange={e => { setForm(f => ({ ...f, identifier: e.target.value })); setErr(""); setUnverified(false); }}
                  placeholder="rahul@gmail.com"
                />
              </>
            )}
          </div>

          {/* Password */}
          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <label style={{ fontSize:13, color:"#94a3b8", fontWeight:500 }}>Password</label>
              {!isStudent && (
                <Link to="/forgot-password" style={{ fontSize:12, color:"#f97316", textDecoration:"none" }}>Forgot password?</Link>
              )}
            </div>
            <div style={{ position:"relative", marginTop:6 }}>
              <input
                required
                type={showPwd ? "text" : "password"}
                style={{ ...inp, paddingRight:44, marginTop:0 }}
                value={form.password}
                onChange={e => { setForm(f => ({ ...f, password:e.target.value })); setErr(""); }}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:0, color:"#94a3b8" }}
              >
                {showPwd ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {isStudent && (
              <div style={{ fontSize:11, color:"#64748b", marginTop:5 }}>
                Forgot password? <Link to="/forgot-password" style={{ color:"#f97316", textDecoration:"none" }}>Reset karo</Link>
              </div>
            )}
          </div>

          {/* Error */}
          {err && (
            <div style={{ color:"#ef4444", fontSize:13, marginBottom:14, background:"#ef444415", padding:"10px 14px", borderRadius:8, border:"1px solid #ef444430", lineHeight:1.5 }}>
              {err}
              {unverified && (
                <div style={{ marginTop:8 }}>
                  {resendDone
                    ? <span style={{ color:"#22c55e" }}>Verification email sent! Check your inbox.</span>
                    : <button type="button" onClick={handleResend} disabled={resendLoading}
                        style={{ background:"none", border:"none", color:"#f97316", cursor:"pointer", fontSize:13, padding:0, textDecoration:"underline" }}>
                        {resendLoading ? "Sending..." : "Resend verification email"}
                      </button>
                  }
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ width:"100%", padding:13, background:"linear-gradient(135deg,#f97316,#ea580c)", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:loading?"not-allowed":"pointer", opacity:loading?0.7:1, transition:"opacity .2s" }}
          >
            {loading ? "Logging in..." : "Login →"}
          </button>
        </form>

        <p style={{ textAlign:"center", marginTop:18, fontSize:13, color:"#94a3b8" }}>
          No account?{" "}
          <Link to="/register" style={{ color:"#f97316", fontWeight:600, textDecoration:"none" }}>Register karo</Link>
        </p>
      </div>
    </div>
  );
}
